'use strict';

import { TeamCampaignEdit } from './TeamCampaignEdit.js';

export class GamificationMyAccountCampaignEdit {
	#form;
	#root;
	#submitButton;
	#initialFormData;
	#initialImageCount;
	#uploadedFiles;
	#isSyncingAdditionalFiles = false;

	constructor() {
		this.#root = document.querySelector(
			'.campaign-details.team-campaign:not(.p2p-campaign)'
		);
		this.#form = this.#root?.querySelector('form');
		this.#submitButton = this.#form?.querySelector('.button-update-campaign');

		if (!this.#form || !this.#submitButton) {
			return;
		}

		// Walk4Friendship (and other themes) use their own campaign edit media handlers.
		if (this.#form.dataset.campaignEditMedia === 'theme') {
			return;
		}

		this.#initialFormData = new FormData(this.#form);
		this.#initialImageCount = this.#getImageCount();
		this.#uploadedFiles = new DataTransfer();

		this.#submitButton.disabled = true;

		const teamCampaignEditHandlesMedia = this.#root.classList.contains(
			'levcharity-team-campaign-edit'
		);

		if (!teamCampaignEditHandlesMedia) {
			this.#bindBannerPreview();
			this.#bindAdditionalImages();
		}

		this.#monitorFormChanges();
		this.#bindSubmitGuard();
	}

	#bindSubmitGuard() {
		this.#form.addEventListener('submit', () => {
			TeamCampaignEdit.releaseHiddenTShirtRequired(this.#form);
		});
	}

	#bindBannerPreview() {
		const bannerInput = this.#root.querySelector(
			'input.banner-image, input[name="banner_image"]'
		);
		if (!bannerInput) {
			return;
		}

		bannerInput.addEventListener('change', () => {
			const file = bannerInput.files?.[0];
			if (!file || !file.type.startsWith('image/')) {
				return;
			}

			const wrapper = this.#root.querySelector('.gamification-campaign-image-wrapper');
			const previewHost = wrapper?.querySelector('.levcharity-main-campaign-preview-host, .campaign_image');
			if (wrapper) {
				wrapper.classList.add('has-featured-image');
				wrapper.hidden = false;
				const nameEl = wrapper.querySelector('.bannered_image_name');
				const metaEl = wrapper.querySelector('.bannered_image_metadata');
				if (nameEl) {
					nameEl.textContent = file.name;
				}
				if (metaEl) {
					metaEl.textContent = '';
					metaEl.hidden = true;
				}
			}

			let previewImg = previewHost?.querySelector('img');
			const blobUrl = URL.createObjectURL(file);
			if (previewImg) {
				if (previewImg.dataset.blobUrl) {
					URL.revokeObjectURL(previewImg.dataset.blobUrl);
				}
				previewImg.dataset.blobUrl = blobUrl;
				previewImg.classList.add('levcharity-main-campaign-preview-img');
				previewImg.removeAttribute('srcset');
				previewImg.removeAttribute('sizes');
				previewImg.src = blobUrl;
			} else if (previewHost) {
				previewImg = document.createElement('img');
				previewImg.className = 'levcharity-main-campaign-preview-img';
				previewImg.dataset.blobUrl = blobUrl;
				previewImg.src = blobUrl;
				previewImg.alt = '';
				previewHost.appendChild(previewImg);
			}

			this.#checkFormState();
		});
	}

	#fileKey( file ) {
		return `${ file.name }|${ file.size }|${ file.lastModified }`;
	}

	#appendAdditionalImageFiles( input, picked ) {
		const previous = Array.from( this.#uploadedFiles.files );
		const seen = new Set( previous.map( ( file ) => this.#fileKey( file ) ) );
		const added = [];

		picked.forEach( ( file ) => {
			const key = this.#fileKey( file );
			if ( seen.has( key ) ) {
				return;
			}
			seen.add( key );
			added.push( file );
		} );

		const all = [ ...previous, ...added ];
		const dt = new DataTransfer();
		all.forEach( ( file ) => dt.items.add( file ) );
		this.#uploadedFiles = dt;
		this.#isSyncingAdditionalFiles = true;
		input.files = this.#uploadedFiles.files;
		this.#isSyncingAdditionalFiles = false;

		return { added, all };
	}

	#appendPendingAdditionalPreviews( imageContainer, files ) {
		files.forEach( ( file, index ) => {
			if ( ! file.type.startsWith( 'image/' ) ) {
				return;
			}
			const blobUrl = URL.createObjectURL( file );
			const wrapper = document.createElement( 'div' );
			wrapper.className = 'image-wrapper levcharity-pending-additional';
			wrapper.dataset.pendingIndex = String(
				Array.from( this.#uploadedFiles.files ).findIndex(
					( stored ) => this.#fileKey( stored ) === this.#fileKey( file )
				)
			);
			wrapper.style.position = 'relative';
			const img = document.createElement( 'img' );
			img.dataset.blobUrl = blobUrl;
			img.src = blobUrl;
			img.alt = '';
			img.style.width = '100px';
			img.style.height = '100px';
			img.style.borderRadius = '4px';
			img.style.objectFit = 'cover';
			img.style.marginRight = '10px';
			const del = document.createElement( 'div' );
			del.className = 'delete-btn remove-additional-image-button';
			del.textContent = 'X';
			wrapper.appendChild( img );
			wrapper.appendChild( del );
			imageContainer.appendChild( wrapper );
		} );
	}

	#renderPendingAdditionalPreviews( imageContainer, files ) {
		imageContainer
			.querySelectorAll(
				'.image-wrapper.levcharity-pending-additional, .image-wrapper:not([data-id])'
			)
			.forEach( ( wrapper ) => {
				const img = wrapper.querySelector( 'img' );
				if ( img?.dataset.blobUrl ) {
					URL.revokeObjectURL( img.dataset.blobUrl );
				}
				wrapper.remove();
			} );

		this.#appendPendingAdditionalPreviews( imageContainer, files );
	}

	#bindAdditionalImages() {
		const fileInput = this.#root.querySelector('#additional_image, .additional-image-edit');
		const imageContainer = this.#root.querySelector('.additional-images-container');

		if (!fileInput || !imageContainer) {
			return;
		}

		if (fileInput.dataset.levcharityAdditionalBound === '1') {
			return;
		}
		fileInput.dataset.levcharityAdditionalBound = '1';

		fileInput.addEventListener('change', () => {
			if (this.#isSyncingAdditionalFiles) {
				return;
			}

			const picked = fileInput.files ? Array.from(fileInput.files) : [];
			if (!picked.length) {
				return;
			}

			const { added } = this.#appendAdditionalImageFiles(fileInput, picked);
			if (added.length) {
				this.#appendPendingAdditionalPreviews(imageContainer, added);
			}
			this.#checkFormState();
		});

		imageContainer.addEventListener('click', (event) => {
			if (!event.target.classList.contains('remove-additional-image-button')) {
				return;
			}

			const wrapper = event.target.closest('.image-wrapper');
			if (!wrapper) {
				return;
			}

			const existingImageId = wrapper.dataset.id;
			if (existingImageId) {
				const removedInput = this.#root.querySelector('#removedImagesInput');
				if (removedInput) {
					const ids = removedInput.value
						? removedInput.value.split(',').filter(Boolean)
						: [];
					if (!ids.includes(existingImageId)) {
						ids.push(existingImageId);
						removedInput.value = ids.join(',');
					}
				}
				wrapper.querySelector('input[name="existing_images[]"]')?.remove();
				wrapper.remove();
			} else if (wrapper.classList.contains('levcharity-pending-additional')) {
				const pendingIndex = Number(wrapper.dataset.pendingIndex);
				const remaining = Array.from(this.#uploadedFiles.files).filter(
					(_file, fileIndex) => fileIndex !== pendingIndex
				);
				const dt = new DataTransfer();
				remaining.forEach((file) => dt.items.add(file));
				this.#uploadedFiles = dt;
				this.#isSyncingAdditionalFiles = true;
				fileInput.files = this.#uploadedFiles.files;
				this.#isSyncingAdditionalFiles = false;
				this.#renderPendingAdditionalPreviews(imageContainer, remaining);
			}

			this.#checkFormState();
		});
	}

	#monitorFormChanges() {
		const inputs = this.#form.querySelectorAll('input, textarea, select');
		inputs.forEach((input) => {
			input.addEventListener('input', () => this.#checkFormState());
			input.addEventListener('change', () => this.#checkFormState());
		});

		if (typeof tinymce !== 'undefined') {
			tinymce.on('AddEditor', (event) => {
				if (event.editor.id === 'campaign_message') {
					const editor = event.editor;

					editor.on('change input keyup', () => {
						editor.save();
						this.#checkFormState();
					});
				}
			});
		}
	}

	#getImageCount() {
		return this.#root.querySelectorAll('.additional-images-container .image-wrapper').length;
	}

	#checkFormState() {
		const requiredFields = this.#form.querySelectorAll('[required]');
		let allFilled = true;

		requiredFields.forEach((field) => {
			if (!field.value.trim()) {
				allFilled = false;
			}
		});

		const currentFormData = new FormData(this.#form);
		let changed = false;

		for (const [key, value] of currentFormData.entries()) {
			const original = this.#initialFormData.get(key) || '';
			if (value !== original) {
				changed = true;
				break;
			}
		}

		const currentImageCount = this.#getImageCount();
		if (currentImageCount !== this.#initialImageCount) {
			changed = true;
		}

		const bannerInput = this.#root.querySelector('input[name="banner_image"]');
		if (bannerInput?.files?.length) {
			changed = true;
		}

		if (this.#uploadedFiles.files.length) {
			changed = true;
		}

		this.#submitButton.disabled = !changed;
	}
}
